import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { usePaymentsStore } from "@/plugins/store";
import { routeNames } from "@/constants/routeNames";
import { useAlert } from "@esenda/core-ui/composables";
import { createSentryBreadcrumb } from "@/helpers/sentry";

const mainRoutes: Array<RouteRecordRaw> = [
    {
        path: "/",
        name: routeNames.DEFAULT,
        component: () => import("@/views/Home.vue"),
    },
    {
        path: "/:shortcode",
        name: routeNames.INVOICE,
        component: () => import("@/views/SupplementaryFormPage.vue"),
        meta: {
            checkShortcode: true,
            checkFormName: false,
        },
    },
    {
        path: "/:shortcode/:form",
        name: routeNames.FORM,
        component: () => import("@/views/SupplementaryFormPage.vue"),
        meta: {
            checkShortcode: true,
            checkFormName: true,
        },
    },
    {
        path: "/:shortcode/:form/supplementary-form",
        name: routeNames.SUPPLEMENTARY_FORM,
        component: () => import("@/views/SupplementaryFormPage.vue"),
        meta: {
            checkShortcode: true,
            checkFormName: true,
        },
    },
    /** Url for one time links */
    {
        path: "/:shortcode/:form/one-time-link/:id",
        name: routeNames.FORM_PAYMENT,
        component: () => import("@/views/FormPayment.vue"),
        meta: {
            checkShortcode: true,
        },
    },
    {
        path: "/:shortcode/:form/payment-methods",
        name: routeNames.PAYMENT_METHOD_SELECTION,
        component: () =>
            import("@/views/payment-methods/PaymentMethodSelection.vue"),
        meta: {
            requiresPaymentMethods: true,
            checkShortcode: true,
            checkFormName: true,
        },
    },
    {
        path: "/:shortcode/:form/payment-methods-disabled",
        name: routeNames.DISABLED_PAYMENT_METHODS,
        component: () =>
            import("@/views/payment-methods/DisabledPaymentMethods.vue"),
        meta: {
            requiresPaymentMethods: true,
            checkShortcode: true,
            checkFormName: true,
        },
    },
    {
        path: "/:catchAll(.*)*",
        name: routeNames.NOT_FOUND,
        component: () => import("@/views/default/NotFoundPage.vue"),
    },
    /* Lloyds Card Payment temporarily disabled on 25.08.2026, see cardForm.vue
    {
        path: "/lloyds-3ds-challenge-iframe",
        name: routeNames.LLOYDS_CHALLENGE_IFRAME,
        component: () =>
            import("@/views/providers/cards/LloydsChallengeIframe.vue"),
    },
    {
        path: "/lloyds-3ds-method-form-iframe",
        name: routeNames.LLOYDS_METHOD_FORM_IFRAME,
        component: () =>
            import("@/views/providers/cards/LloydsMethodFormIframe.vue"),
    },
    */
    {
        path: "/:shortcode/:form/payment/status/:id",
        name: routeNames.PAYMENT_STATUS,
        component: () => import("@/views/PaymentStatus.vue"),
        meta: { checkShortcode: true, checkFormName: true, checkId: true },
    },
    {
        path: "/unauthorised",
        name: routeNames.ACCESS_DENIED,
        component: () => import("@/views/default/AccessDeniedPage.vue"),
    },
    {
        path: "/terms-and-conditions",
        name: routeNames.TERMS_AND_CONDITIONS,
        component: () => import("@/views/default/TermsAndConditions.vue"),
    },
    {
        path: "/privacy-policy",
        name: routeNames.PRIVACY_POLICY,
        component: () => import("@/views/default/PrivacyPolicy.vue"),
    },
    {
        path: "/:shortcode/payment-request",
        name: routeNames.OLD_PAYMENT_REQUEST_LINK,
        component: () => import("@/views/OldPaymentRequestLink.vue"),
        meta: {
            checkShortcode: true,
            checkFormName: false,
            checkId: false,
            requiresRedirectToNewLink: true,
        },
    },
    {
        path: "/reeds",
        name: routeNames.REEDS_LANDING_PAGE,
        component: () => import("@/views/static/ReedsLandingPage.vue"),
        meta: { checkShortcode: false, checkFormName: false, checkId: false },
    },
    // E-mandates
    {
        path: "/:shortcode/e-mandates/:accountAlias",
        name: routeNames.E_MANDATES_LANDING_PAGE,
        component: () => import("@/views/LoginPage.vue"),
        meta: {
            checkShortcode: true,
            checkFormName: false,
            requiresAccountAlias: true,
        },
    },
    {
        path: "/:shortcode/e-mandates/:accountAlias/form",
        name: routeNames.E_MANDATES_FORM,
        component: () => import("@/views/eMandates/Form.vue"),
        meta: {
            checkShortcode: true,
            checkFormName: false,
            requiresAccountAlias: true,
        },
    },
    {
        path: "/:shortcode/e-mandates/:accountAlias/review-details",
        name: routeNames.E_MANDATES_REVIEW_DETAILS,
        component: () => import("@/views/eMandates/ReviewDetails.vue"),
        meta: {
            checkShortcode: true,
            checkFormName: false,
            requiresAccountAlias: true,
        },
    },
    {
        path: "/:shortcode/e-mandates/:accountAlias/confirmation",
        name: routeNames.E_MANDATES_CONFIRMATION,
        component: () => import("@/views/eMandates/Confirmation.vue"),
        meta: {
            checkShortcode: true,
            checkFormName: false,
            requiresAccountAlias: true,
        },
    },
    {
        path: "/auth/redirect",
        name: routeNames.AUTH_REDIRECT,
        component: () => import("@/views/auth/Redirect.vue"),
        meta: { checkShortcode: false, checkFormName: false },
    },
    {
        path: "/auth/sign-out",
        name: routeNames.AUTH_SIGN_OUT,
        component: () => import("@/views/auth/SignOut.vue"),
        meta: { checkShortcode: false, checkFormName: false },
    },
    {
        path: "/:shortcode/:form/payment-link-unavailable",
        name: routeNames.PAYMENT_LINK_UNAVAILABLE,
        component: () => import("@/views/default/PaymentLinkUnavailable.vue"),
        meta: {
            checkShortcode: true,
            checkFormName: true,
        },
    },
];

const routes = mainRoutes;

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        }
        // Same-path navigations (e.g. hash-only replace) should not scroll
        if (to.path === from.path) {
            return false;
        }
        return { top: 0, left: 0 };
    },
});

router.beforeEach((to, from) => {
    const paymentStore = usePaymentsStore();

    /** Gets the meta properties associated to the route */
    const requiresPaymentMethods = to.matched.some(
        (x) => x.meta.requiresPaymentMethods,
    );
    const requiresShortcodeParam = to.matched.some(
        (x) => x.meta.checkShortcode,
    );
    const requiresFormNameParam = to.matched.some((x) => x.meta.checkFormName);
    const requiresIdParam = to.matched.some((x) => x.meta.checkId);
    const requiresRedirectToNewLink = to.matched.some(
        (x) => x.meta.requiresRedirectToNewLink,
    );
    const requiresAccountAlias = to.matched.some(
        (x) => x.meta.requiresAccountAlias,
    );

    /**
     * Redirects to 404 if the route requiresShortcodeParam and the
     * shortcode param is null or undefined
     */
    if (requiresShortcodeParam) {
        if (["null", "undefined"].includes(String(to.params.shortcode))) {
            return "/";
        }
    }

    /**
     * Redirects to 404 if the route requiresFormNameParam and the
     * form name param is null or undefined
     */
    if (requiresFormNameParam) {
        if (["null", "undefined"].includes(String(to.params.form))) {
            return "/";
        }
    }

    /**
     * Redirects to 404 if the route requiresIdParam and the
     * id param is null or undefined
     */
    if (requiresIdParam) {
        if (["null", "undefined"].includes(String(to.params.id))) {
            return "/";
        }
    }

    /**
     * Redirects to the first page if the route requiresPaymentMethods and
     * the payment method list in the session is null
     */
    if (requiresPaymentMethods) {
        if (paymentStore.paymentMethods === null && from.name !== "Form") {
            return `/${to.params.shortcode}/${to.params.form}`;
        }
    }

    /**
     * Redirects to the form page if the route requiresRedirectToNewLink and
     * it adds the payment_reference param as the form name
     */
    if (requiresRedirectToNewLink) {
        return `/${to.params.shortcode}/${to.query.payment_reference}`;
    }

    /**
     * Redirects to 404 if the route requiresAccountAlias and the
     * accountAlias param is null or undefined
     */
    if (requiresAccountAlias) {
        if (["null", "undefined"].includes(String(to.params.accountAlias))) {
            return "/";
        }
    }
});

const CHUNK_ERROR_PATTERNS = [
    "Importing a module script failed",
    "Failed to fetch dynamically imported module",
    "error loading dynamically imported module",
    "text/html' is not a valid JavaScript MIME type",
    "text/html is not a valid JavaScript MIME type",
    // Vue Router's secondary error when a chunk load failure causes the component
    // factory to return undefined. Always a consequence of one of the errors above.
    "Couldn't resolve component",
];

const PRELOAD_RELOADED_KEY = "vite-preload-reloaded";

/**
 * Global router error handler to catch chunk load failures, which can occur
 * when a new deployment is released while a user has the app open. The handler attempts
 * a one-shot reload to get the latest assets, and if that fails, shows an alert
 * and logs the error to Sentry for investigation.
 * Note: Vite's native handling of preload errors (via the vite:preloadError event)
 * is also used to catch failures during the initial app load, which may occur before this router handler is active.
 */
router.onError((error, to) => {
    const { showAlert } = useAlert();
    const message = error?.message ?? "";
    const isChunkError = CHUNK_ERROR_PATTERNS.some((p) => message.includes(p));

    if (!isChunkError) return;

    const alreadyReloaded = sessionStorage.getItem(PRELOAD_RELOADED_KEY);
    if (!alreadyReloaded) {
        sessionStorage.setItem(PRELOAD_RELOADED_KEY, "1");
        // Hard-navigate to the intended route so the browser re-fetches all chunks.
        // Using assign() rather than reload() so the user lands on the correct page.
        // Prepend origin to prevent scheme-relative open-redirect if fullPath
        // were to start with "//" (e.g. "//evil.com").
        window.location.assign(window.location.origin + to.fullPath);
    } else {
        showAlert("alerts.newAppVersion");
        // Record as a breadcrumb only — this branch fires on every deployment
        // for users with the tab open (expected behaviour), so creating a Sentry
        // issue here would generate noise on every deploy. The showAlert already
        // informs the user; the breadcrumb provides context in session replays.
        createSentryBreadcrumb(
            "error",
            `router.onError: chunk failed to load — original: ${message}, route: ${to.fullPath}`,
            "warning",
        );
    }
});

router.afterEach((_to, _from, failure) => {
    // Clear the one-shot reload flag only after a SUCCESSFUL navigation.
    // If the navigation failed (e.g. a chunk load error), the flag set by
    // router.onError must be preserved so the reload loop guard still works.
    if (!failure) {
        sessionStorage.removeItem(PRELOAD_RELOADED_KEY);
    }
});

export const redirectTo404 = () => {
    router.push({ name: routeNames.NOT_FOUND });
};

export const redirectToAccessDenied = () => {
    router.push({ name: routeNames.ACCESS_DENIED });
};

export const redirectToPaymentStatus = (
    shortcode: string,
    formName: string,
    id: string,
) => {
    router.push({
        name: routeNames.PAYMENT_STATUS,
        params: {
            shortcode: shortcode,
            form: formName,
            id: id,
        },
    });
};

export const redirectToPath = (path: string) => {
    router.push(path);
};

export const redirectToRoute = (name: string, params: any) => {
    router.push({
        name: name,
        params: params,
    });
};

export default router;
